import { fold } from './matchingSections';

/**
 * How the printed booklet opens: the cover alone, page 1 alone (it faces the inside cover), then each even page
 * on the left with the odd page after it on the right.
 *
 * @param {Array<{number: number|null}>} pages in book order
 * @returns {Array<Array<object>>} spreads, each one or two pages
 */
export function bookSpreads(pages) {
  const spreads = [];

  pages.forEach(page => {
    const previous = spreads[spreads.length - 1];
    const facing = previous && previous.length === 1 && previous[0].number;
    const isRightHand = page.number % 2 === 1 && facing === page.number - 1 && facing % 2 === 0;

    if (isRightHand) {
      previous.push(page);
    } else {
      spreads.push([page]);
    }
  });

  return spreads;
}

/**
 * Which side of an open booklet a page sits on: the cover and odd pages on the right, even pages on the left.
 *
 * @returns {'left'|'right'}
 */
export function sideOf(page) {
  return page.number === null || page.number % 2 === 1 ? 'right' : 'left';
}

/**
 * A spread as the two sides of the open booklet; a side with no page is null (the cover and page 1 stand alone on
 * the right).
 *
 * @returns {{left: object|null, right: object|null}}
 */
export function spreadSides(spread) {
  const sides = { left: null, right: null };
  spread.forEach(page => { sides[sideOf(page)] = page; });
  return sides;
}

/**
 * Whether the booklet is closed, showing only its cover. Otherwise an empty side is the back of a page already
 * turned (the inside of the cover beside page 1) and shows as paper.
 */
export function isClosed(spread) {
  return spread.some(page => page.number === null);
}

/**
 * The text section to read for page `number`: the first one that starts on it, or else the one running through it
 * from an earlier page.
 */
export function sectionOnPage(sections, number) {
  return sections.find(section => section.first_page === number)
    || sections.find(section => section.first_page <= number && number <= section.last_page);
}

/** Where `number` is in `units` (pages, or spreads of pages); the cover when null or not found. */
export function unitIndexOf(units, number) {
  const index = units.findIndex(unit => [].concat(unit).some(page => page.number === number));
  return Math.max(0, index);
}

/**
 * The section an index entry points to on page `number`. The sections on that page are ranked: a heading naming
 * the topic first, then by how often the text mentions it, then one that starts on the page over one running
 * through it. With no mention anywhere, the first section on the page.
 *
 * @param {string} topic the index entry, like "Damage, figuring"
 */
export function sectionForTopic(sections, number, topic) {
  const onPage = sections.filter(section => section.first_page <= number && number <= section.last_page);
  const words = fold(topic).split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  const mentions = text => words.reduce((count, word) => count + (fold(text).split(word).length - 1), 0);
  const score = section => (
    (words.every(word => fold(section.heading).includes(word)) ? 1000 : 0)
    + mentions(section.html.replace(/<[^>]+>/g, ' '))
    + (section.first_page === number ? 0.5 : 0)
  );
  const best = onPage.reduce((top, section) => (!top || score(section) > score(top) ? section : top), null);
  return best && score(best) >= 1 ? best : sectionOnPage(sections, number);
}
